import { Box } from "@mui/material";
import { useTheme } from "../../../context/ThemeContext";
import { TABULAR_NUMERALS } from "../../general/dataAtoms/shared.ts";

export interface SplitBarPart {
  key: string;
  count: number;
  color: string;
  // what the part says: its count and what is counted, already worded
  label: string;
}

/**
 * The dots of a scatter counted by zone, as one bar under it: a part per zone
 * that has dots, in the order given and as wide as its count. The general
 * split bar names a part and then its share; here each part is a phrase of
 * its own that begins with its count.
 */
export const SplitBar = ({ parts }: { parts: readonly SplitBarPart[] }) => {
  const { theme } = useTheme();
  const total = parts.reduce((sum, { count }) => sum + count, 0);
  return (
    <Box
      sx={{
        display: "flex",
        height: theme.spacing(3.75),
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: "action.hover",
        mt: 1.5,
      }}
    >
      {parts
        .filter(({ count }) => count > 0)
        .map(({ key, count, color, label }) => (
          <Box
            key={key}
            // a part too narrow for its phrase still says it on hover
            title={label}
            sx={{
              width: `${(count / total) * 100}%`,
              // the air around the phrase is part of the width of its part
              boxSizing: "border-box",
              px: 1,
              bgcolor: color,
              color: "text.primary",
              typography: "caption",
              fontWeight: 600,
              lineHeight: theme.spacing(3.75),
              textAlign: "center",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              ...TABULAR_NUMERALS,
            }}
          >
            {label}
          </Box>
        ))}
    </Box>
  );
};
